import { Head, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { useState } from "react"

import { Avatar, Field, PageHeader, Section, StatusBadge } from "@/components/steel"
import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatCurrency } from "@/lib/utils"

type EmployeeRow = {
  id: string
  name: string
  role: string
  department: string
  avatar: string
  hourlyRate: number
  status: string
  totalHours: number
  projectCount: number
}

const EMPTY_FORM = { name: "", role: "", department: "", hourlyRate: "", status: "active" }

export default function EmployeesIndex({ employees }: { employees: EmployeeRow[] }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)

  const activeCount = employees.filter((e) => e.status === "active").length

  const submit = () => {
    router.post(
      "/employees",
      {
        name: form.name,
        role: form.role,
        department: form.department,
        hourly_rate: form.hourlyRate,
        status: form.status,
      },
      {
        onSuccess: () => {
          setOpen(false)
          setForm(EMPTY_FORM)
        },
      },
    )
  }

  return (
    <>
      <Head title="Employees" />

      <PageHeader
        title="Employees"
        meta={`${employees.length} on the roster · ${activeCount} active`}
        actions={
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus />
            New employee
          </Button>
        }
      />

      <Section className="pb-7 pt-3.5">
        <DataTable>
          <Thead>
            <Th>Employee</Th>
            <Th>Role</Th>
            <Th>Department</Th>
            <Th align="right">Rate</Th>
            <Th align="right">Hours</Th>
            <Th align="right">Projects</Th>
            <Th align="right">Status</Th>
          </Thead>
          <tbody>
            {employees.map((employee) => (
              <Tr key={employee.id} onClick={() => router.visit(`/team/employees/${employee.id}`)}>
                <Td>
                  <span className="flex items-center gap-2.5">
                    <Avatar initials={employee.avatar} />
                    <span>
                      <span className="block text-body font-medium">{employee.name}</span>
                      <span className="num block text-meta text-muted-foreground">{employee.id}</span>
                    </span>
                  </span>
                </Td>
                <Td>{employee.role}</Td>
                <Td muted>{employee.department}</Td>
                <Td align="right" num>
                  {formatCurrency(employee.hourlyRate)}
                </Td>
                <Td align="right" num muted>
                  {employee.totalHours}h
                </Td>
                <Td align="right" num muted>
                  {employee.projectCount}
                </Td>
                <Td align="right">
                  <StatusBadge status={employee.status} />
                </Td>
              </Tr>
            ))}
          </tbody>
        </DataTable>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New employee</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Jane Smith"
              />
            </Field>
            <Field label="Role">
              <Input
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                placeholder="Electrician"
              />
            </Field>
            <Field label="Department">
              <Input
                value={form.department}
                onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                placeholder="Operations"
              />
            </Field>
            <Field label="Hourly rate ($)">
              <Input
                className="num"
                type="number"
                min="0"
                step="0.01"
                value={form.hourlyRate}
                onChange={(e) => setForm((f) => ({ ...f, hourlyRate: e.target.value }))}
                placeholder="45.00"
              />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_leave">On leave</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={submit} disabled={!form.name || !form.role || !form.hourlyRate}>
              Create employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
